// apps/webapp/src/screens/talk/talk-zone-picker.ts
/**
 * Persistent Talk zone picker: a scrollable chip row (Home, saved zones, Nearby).
 * Owns its element across app re-renders so unread badges can update in place
 * (setUnread) without rebuilding the feed. render() rebuilds the chips (zone set /
 * selection changed); setUnread() only repaints badges.
 */
import type { TalkZone, TalkZoneKind } from "./talk-zones.js";

type T = (k: string, p?: Record<string, string | number>) => string;

function fallbackKey(kind: TalkZoneKind): string {
  return kind === "home" ? "talk.zone.home" : kind === "live" ? "talk.zone.nearby" : "you.loc.placeUnnamed";
}

export class ZonePicker {
  readonly element: HTMLElement;
  #t: T;
  #onZone: (id: string) => void;

  constructor(opts: { t: T; onZone: (id: string) => void }) {
    this.#t = opts.t;
    this.#onZone = opts.onZone;
    this.element = document.createElement("div");
    this.element.className = "talk-zones";
    this.element.setAttribute("role", "tablist");
  }

  render(zones: TalkZone[], activeId: string, unread: (id: string) => number): void {
    this.element.replaceChildren();
    for (const z of zones) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.setAttribute("data-zone", z.id);
      chip.setAttribute("role", "tab");
      const active = z.id === activeId;
      chip.className = `talk-zone__chip${active ? " talk-zone__chip--active" : ""}`;
      chip.setAttribute("aria-selected", String(active));
      chip.textContent = z.name || this.#t(fallbackKey(z.kind));
      this.#applyBadge(chip, active ? 0 : unread(z.id));
      chip.addEventListener("click", () => this.#onZone(z.id));
      this.element.append(chip);
    }
  }

  setUnread(unread: (id: string) => number, activeId: string): void {
    const chips = this.element.children;
    for (let i = 0; i < chips.length; i++) {
      const chip = chips[i];
      if (!(chip instanceof HTMLElement)) continue;
      const id = chip.getAttribute("data-zone") ?? "";
      this.#applyBadge(chip, id === activeId ? 0 : unread(id));
    }
  }

  #applyBadge(chip: HTMLElement, n: number): void {
    const existing = chip.querySelector("sup");
    if (n <= 0) { existing?.remove(); return; }
    const sup = existing ?? chip.appendChild(document.createElement("sup"));
    sup.className = "talk-zone__badge";
    sup.textContent = n > 9 ? "9+" : String(n);
  }
}
